import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Booking } from '../../api';
import { Card, Avatar } from '../ui';
import { parseUtc } from '../../utils/dates';
import { useActiveRentals } from './useActiveRentals';
import BookingEditModal from '../BookingEditModal';
import RowTooltip from './RowTooltip';

function getInitials(name: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2);
  return (parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '');
}

function describeOngoing(startsAt: string, endsAt: string): { primary: string; secondary: string; startLabel: string; endLabel: string } {
  const start = parseUtc(startsAt);
  const end = parseUtc(endsAt);
  const now = new Date();

  // Primær: hvor længe siden starten
  const sinceMs = now.getTime() - start.getTime();
  const sinceHours = Math.floor(sinceMs / 3_600_000);
  const sinceDays = Math.floor(sinceMs / 86_400_000);

  let primary: string;
  if (sinceDays >= 1) {
    primary = sinceDays === 1 ? '1 dag' : `${sinceDays} dage`;
  } else if (sinceHours >= 1) {
    primary = `${sinceHours} t`;
  } else {
    primary = 'lige nu';
  }

  // Sekundær: kort label om afleveringstidspunkt
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowStart = new Date(todayStart.getTime() + 86_400_000);
  const dayAfter = new Date(tomorrowStart.getTime() + 86_400_000);

  let secondary: string;
  if (end < tomorrowStart) {
    secondary = 'aflev. i dag';
  } else if (end < dayAfter) {
    secondary = 'aflev. i morgen';
  } else {
    const dateStr = end.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
    secondary = `aflev. ${dateStr}`;
  }

  // Tooltip-kontekst (label til popup)
  const startLabel = start.toLocaleString('da-DK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const endLabel = end.toLocaleString('da-DK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  return { primary, secondary, startLabel, endLabel };
}

export default function OngoingRentalsWidget() {
  const { t } = useTranslation();

  const { data, loading, error, refresh } = useActiveRentals();
  const [selected, setSelected] = useState<Booking | null>(null);

  // Sortér efter starts_at DESC (senest påbegyndte først)
  const items = (data ?? [])
    .slice()
    .sort((a, b) => b.starts_at.localeCompare(a.starts_at))
    .slice(0, 8);

  const handleSaved = () => {
    refresh();
    setSelected(null);
  };

  return (
    <>
      <Card padded className="h-full">
        <div className="flex items-baseline justify-between mb-1">
          <h3 className="text-base font-semibold text-ink-900 tracking-tight">
            {t('dashboard.ongoing_rentals_title', { defaultValue: 'Igangværende udlejninger' })}
          </h3>
          {items.length > 0 && (
            <span className="text-xs text-ink-500">{items.length} aktive</span>
          )}
        </div>

        {loading && (
          <p className="text-sm text-ink-500 mt-4">…</p>
        )}
        {error && (
          <p className="text-sm text-rose-700 mt-4">Kunne ikke hente data</p>
        )}
        {!loading && !error && items.length === 0 && (
          <p className="text-sm text-ink-500 mt-4">Ingen igangværende udlejninger</p>
        )}

        <div className="mt-3 -mx-1">
          {items.map(item => {
            const when = describeOngoing(item.starts_at, item.ends_at);
            return (
              <button
                key={item.id}
                onClick={() => setSelected(item)}
                className="relative group/row w-full text-left flex items-center gap-3 px-[15px] py-3 border-b border-cream-100 last:border-b-0 hover:bg-cream-100 transition-colors"
              >
                <Avatar initials={getInitials(item.renter_name)} size="md" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-ink-900 truncate">
                    {item.renter_name || '—'}
                  </div>
                  <div className="text-xs text-ink-500 truncate font-mono">
                    {item.registration_number ?? '—'}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold text-ink-900">{when.primary}</div>
                  <div className="text-xs text-ink-500">{when.secondary}</div>
                </div>

                <RowTooltip>
                  <div className="font-semibold mb-1">{item.renter_name || '—'}</div>
                  <div className="font-mono text-ink-500 mb-1.5">{item.registration_number ?? '—'}</div>
                  <div className="space-y-0.5">
                    <div><span className="text-ink-500">Start:</span> {when.startLabel}</div>
                    <div><span className="text-ink-500">Aflev:</span> {when.endLabel}</div>
                  </div>
                </RowTooltip>
              </button>
            );
          })}
        </div>
      </Card>

      {selected && (
        <BookingEditModal
          booking={selected}
          onClose={() => setSelected(null)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}
